import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { ArrowLeft, Calendar, User, ArrowRight, BookOpen } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { SiteNav } from '@/components/SiteNav';
import { SiteFooter } from '@/components/SiteFooter';
import { getPosts, BlogPost } from '@/lib/store';

function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

// Simple markdown renderer (bold, headings, paragraphs)
function renderMarkdown(content: string): JSX.Element[] {
  const lines = content.split('\n');
  const elements: JSX.Element[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) { i++; continue; }

    if (line.startsWith('### ')) {
      elements.push(<h3 key={i} className="text-xl font-bold text-foreground mt-6 mb-2">{line.slice(4)}</h3>);
    } else if (line.startsWith('## ')) {
      elements.push(<h2 key={i} className="text-2xl font-bold text-foreground mt-8 mb-3">{line.slice(3)}</h2>);
    } else if (line.startsWith('# ')) {
      elements.push(<h1 key={i} className="text-3xl font-bold text-foreground mt-8 mb-4">{line.slice(2)}</h1>);
    } else if (line.startsWith('**') && line.endsWith('**')) {
      elements.push(<p key={i} className="text-foreground font-bold my-3">{line.slice(2, -2)}</p>);
    } else {
      // Inline bold
      const parts = line.split(/(\*\*[^*]+\*\*)/g);
      const rendered = parts.map((p, j) =>
        p.startsWith('**') && p.endsWith('**')
          ? <strong key={j}>{p.slice(2, -2)}</strong>
          : p
      );
      elements.push(<p key={i} className="text-muted-foreground leading-relaxed my-3">{rendered}</p>);
    }
    i++;
  }
  return elements;
}

// ── Blog Listing ──────────────────────────────────────────────────────────────
export function BlogListPage() {
  useSeoMeta({
    title: 'Field Blog — Dakota Horizon Outdoors',
    description: 'Hunting tips, fishing reports, gear reviews, and stories from the field.',
  });

  const posts = getPosts().filter((p) => p.published).sort((a, b) => b.publishedAt - a.publishedAt);

  return (
    <div className="min-h-screen bg-background">
      <SiteNav />

      {/* Header */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(135deg, rgba(10,20,5,0.90) 0%, rgba(30,20,10,0.80) 100%), 
              url('https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=1200&q=80')`,
          }}
        />
        <div className="relative container mx-auto px-4 text-center">
          <Badge className="bg-amber-600/30 text-amber-400 border-amber-600/50 mb-4">Field Notes</Badge>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">The Field Blog</h1>
          <p className="text-stone-300 text-lg max-w-xl mx-auto">
            Hunting tips, fishing reports, gear reviews, and stories straight from the field.
          </p>
        </div>
      </section>

      {/* Posts */}
      <main className="py-16">
        <div className="container mx-auto px-4">
          {posts.length === 0 ? (
            <Card className="border-dashed max-w-md mx-auto">
              <CardContent className="py-16 text-center">
                <BookOpen className="h-10 w-10 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No posts published yet. Check back soon!</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {posts.map((post, idx) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className={`group block ${idx === 0 ? 'md:col-span-2 lg:col-span-3' : ''}`}
                >
                  <Card className={`overflow-hidden border-border/50 h-full product-card-hover ${idx === 0 ? 'lg:flex' : ''}`}>
                    {/* Image */}
                    <div className={`bg-gradient-to-br from-stone-100 to-stone-200 dark:from-stone-800 dark:to-stone-900 overflow-hidden ${idx === 0 ? 'lg:w-1/2 aspect-video lg:aspect-auto' : 'aspect-video'}`}>
                      {post.imageUrl ? (
                        <img
                          src={post.imageUrl}
                          alt={post.title}
                          className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="h-full flex items-center justify-center">
                          <img src="/images/DHO.jpg" alt="DHO" className="h-20 w-20 object-contain opacity-20" />
                        </div>
                      )}
                    </div>
                    {/* Content */}
                    <CardContent className={`p-5 flex flex-col ${idx === 0 ? 'lg:flex-1' : ''}`}>
                      {idx === 0 && <Badge className="bg-amber-700/20 text-amber-700 border-amber-700/30 w-fit mb-3">Featured</Badge>}
                      <h2 className={`font-bold text-foreground leading-snug group-hover:text-amber-700 transition-colors ${idx === 0 ? 'text-2xl lg:text-3xl mb-3' : 'text-base mb-2'}`}>
                        {post.title}
                      </h2>
                      <p className="text-muted-foreground text-sm leading-relaxed line-clamp-3 flex-1">{post.excerpt}</p>
                      <div className="flex items-center gap-3 mt-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1"><User className="h-3 w-3" />{post.author}</span>
                        <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{formatDate(post.publishedAt)}</span>
                      </div>
                      <div className="flex items-center gap-1 text-amber-700 text-sm font-medium mt-3 group-hover:gap-2 transition-all">
                        Read More <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}

// ── Single Post ───────────────────────────────────────────────────────────────
export function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const posts = getPosts().filter((p) => p.published);
  const post = posts.find((p) => p.slug === slug);

  useSeoMeta({
    title: post ? `${post.title} — Dakota Horizon Outdoors` : 'Post Not Found',
    description: post?.excerpt,
  });

  if (!post) {
    return (
      <div className="min-h-screen bg-background">
        <SiteNav />
        <main className="pt-32 container mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-bold mb-4">Post not found.</h1>
          <Link to="/blog" className="text-amber-600 hover:underline">← Back to Blog</Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const related = posts.filter((p) => p.id !== post.id).slice(0, 3);

  return (
    <div className="min-h-screen bg-background">
      <SiteNav />

      {/* Hero */}
      <section className="pt-24">
        {post.imageUrl ? (
          <div
            className="relative h-64 md:h-96 bg-cover bg-center"
            style={{
              backgroundImage: `linear-gradient(to bottom, transparent 40%, rgba(0,0,0,0.7) 100%), url('${post.imageUrl}')`,
            }}
          />
        ) : (
          <div className="h-16" />
        )}
      </section>

      {/* Content */}
      <main className="py-12">
        <div className="container mx-auto px-4 max-w-2xl">
          <Link to="/blog" className="inline-flex items-center gap-1 text-muted-foreground hover:text-amber-600 text-sm mb-8 transition-colors">
            <ArrowLeft className="h-4 w-4" /> Back to Blog
          </Link>

          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4 leading-tight">{post.title}</h1>

          <div className="flex items-center gap-4 text-sm text-muted-foreground mb-8 pb-8 border-b border-border">
            <span className="flex items-center gap-1.5"><User className="h-4 w-4" />{post.author}</span>
            <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4" />{formatDate(post.publishedAt)}</span>
          </div>

          <div className="prose-like text-foreground">
            {renderMarkdown(post.content)}
          </div>
        </div>
      </main>

      {/* Related Posts */}
      {related.length > 0 && (
        <section className="py-12 bg-muted/40">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl font-bold text-foreground mb-6">More from the Field</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((p) => (
                <Link key={p.id} to={`/blog/${p.slug}`} className="group block">
                  <Card className="overflow-hidden border-border/50 product-card-hover h-full">
                    <div className="aspect-video bg-muted overflow-hidden">
                      {p.imageUrl ? (
                        <img src={p.imageUrl} alt={p.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      ) : (
                        <div className="h-full flex items-center justify-center">
                          <img src="/images/DHO.jpg" alt="DHO" className="h-12 w-12 opacity-20" />
                        </div>
                      )}
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-semibold text-foreground text-sm leading-snug group-hover:text-amber-700 transition-colors line-clamp-2">{p.title}</h3>
                      <p className="text-muted-foreground text-xs mt-1">{formatDate(p.publishedAt)}</p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <SiteFooter />
    </div>
  );
}
